/**
 * Iter 84.4 / Phase E5 Sprint 4 — Compliance Health Dashboard (D39).
 *
 * Read-only landing page surfacing the operator's compliance posture:
 *   • Hero score (0-100) + Grade
 *   • 7 engine status cards (PF · ESI · PT · LWF · 24Q · Form 16 · Bonus)
 *   • Filing calendar with traffic-light states (GREEN / AMBER / RED / GREY)
 *   • Buckets — Next 7d, Next 30d, Current Quarter, Overdue
 *   • Exceptions list (Missing PAN/UAN/ESIC, Unreconciled TDS, Coverage gaps, ...)
 *
 * No writes — every action is a drill-through to the relevant console.
 */
import { useCallback, useEffect, useState } from "react";
import {
    ArrowsClockwise, DownloadSimple, CheckCircle, Warning, XCircle, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";

const STATUS_COLORS = {
    GREEN: "bg-emerald-100 text-emerald-800 border-emerald-200",
    AMBER: "bg-amber-100 text-amber-800 border-amber-200",
    RED: "bg-rose-100 text-rose-800 border-rose-200",
    GREY: "bg-slate-100 text-slate-600 border-slate-200",
};

const ENGINE_STATUS_COLORS = {
    ON_TRACK: "text-emerald-700",
    BEHIND: "text-amber-700",
    OVERDUE: "text-rose-700",
    NOT_DUE: "text-slate-500",
    IN_PROGRESS: "text-blue-700",
    ISSUED: "text-emerald-700",
    FY_NOT_INITIATED: "text-rose-700",
};

const GRADE_COLOR = {
    A: "bg-emerald-500 text-white",
    B: "bg-blue-500 text-white",
    C: "bg-amber-500 text-white",
    D: "bg-orange-500 text-white",
    F: "bg-rose-600 text-white",
};

const ENGINE_LABELS = {
    PF_ECR: "PF",
    ESI_RETURN: "ESI",
    PT_CHALLAN: "PT",
    LWF_RETURN: "LWF",
    FORM_24Q: "Form 24Q",
    FORM_16B: "Form 16",
    BONUS_REGISTER: "Bonus",
};

const ENGINE_LINKS = {
    PF_ECR: "/admin/compliance/console",
    ESI_RETURN: "/admin/compliance/console",
    PT_CHALLAN: "/admin/compliance/console",
    LWF_RETURN: "/admin/compliance/console",
    FORM_24Q: "/admin/compliance/tds-returns",
    FORM_16B: "/admin/compliance/tds-returns",
    BONUS_REGISTER: "/admin/compliance/bonus",
};

const CURRENT_FY_DEFAULT = "2025-2026";

export default function ComplianceHealthDashboard() {
    const [fy, setFy] = useState(CURRENT_FY_DEFAULT);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(
                `/compliance/health-snapshot?financial_year=${fy}&nocache=true`,
            );
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load compliance health"));
        } finally {
            setLoading(false);
        }
    }, [fy]);

    useEffect(() => { reload(); }, [reload]);

    const handleExportCsv = async () => {
        try {
            const r = await api.get(
                `/compliance/health-snapshot/export.csv?financial_year=${fy}`,
                { responseType: "blob" },
            );
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ComplianceHealth_${fy}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Compliance Health CSV downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "CSV export failed"));
        }
    };

    return (
        <div className="p-6 space-y-5" data-testid="compliance-health-dashboard">
            <PageHeader
                title="Compliance Health"
                subtitle={data ?
                    `FY ${data.financial_year} · as of ${data.as_of} · score v${data.score_version}` :
                    "Operator landing dashboard — overdue, due-next, and blocked at a glance"
                }
                actions={
                    <div className="flex gap-2 items-end">
                        <div>
                            <Label className="text-xs">FY</Label>
                            <Input
                                value={fy}
                                onChange={(e) => setFy(e.target.value)}
                                className="font-mono w-32 h-8"
                                data-testid="compliance-health-fy-input"
                            />
                        </div>
                        <Button size="sm" variant="outline" onClick={reload}
                            disabled={loading} data-testid="compliance-health-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleExportCsv}
                            disabled={!data} data-testid="compliance-health-export-btn">
                            <DownloadSimple size={14} className="mr-1" /> Export CSV
                        </Button>
                    </div>
                }
            />

            {!data && !loading && (
                <div className="text-center text-slate-400 p-8" data-testid="compliance-health-empty">
                    No snapshot loaded — adjust FY and Refresh.
                </div>
            )}

            {data && (
                <>
                    {/* Iter 111.1 · obs #7 — Fresh-system empty state. When
                        no payroll has been processed yet, `expected_to_date`
                        will be 0 and every category is GREY. In that case,
                        render an informational panel instead of Grade F. */}
                    {(data.expected_to_date || 0) === 0 && (data.completed_to_date || 0) === 0 ? (
                        <div className="border border-slate-200 rounded p-6 bg-slate-50 text-center"
                             data-testid="compliance-health-not-yet-processed">
                            <div className="text-lg font-semibold text-slate-800">Payroll not processed yet</div>
                            <div className="text-sm text-slate-600 mt-1">
                                No compliance filings are expected until the first payroll run of FY {data.financial_year} is calculated. Once you process your first cycle, this dashboard will start reflecting real-time compliance health.
                            </div>
                            <div className="text-[11px] text-slate-400 mt-2 font-mono">
                                score_version={data.score_version} · snapshot generated {data.computed_at?.slice(0, 16).replace("T", " ")}
                            </div>
                        </div>
                    ) : (
                    /* Hero score */
                    <div className="border rounded p-5 bg-gradient-to-r from-slate-50 to-white flex items-center gap-6"
                        data-testid="compliance-health-hero">
                        <div className={`text-5xl font-mono font-bold tracking-tight ${
                            data.health_score >= 95 ? "text-emerald-600" :
                            data.health_score >= 85 ? "text-blue-600" :
                            data.health_score >= 70 ? "text-amber-600" :
                            data.health_score >= 50 ? "text-orange-600" : "text-rose-600"
                        }`} data-testid="compliance-health-score">
                            {data.health_score}%
                        </div>
                        <div>
                            <div className={`inline-block px-3 py-1 rounded text-sm font-semibold ${GRADE_COLOR[data.health_grade] || "bg-slate-200"}`}
                                data-testid="compliance-health-grade">
                                Grade {data.health_grade}
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                                Completed <strong>{data.completed_to_date}</strong> of <strong>{data.expected_to_date}</strong> filings due to date.
                                {data.exception_penalty > 0 && (
                                    <span className="ml-2 text-amber-700">
                                        (exception penalty −{data.exception_penalty}pt)
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-1 font-mono">
                                score_version={data.score_version} · snapshot_version={data.snapshot_version} · computed {data.computed_at?.slice(0, 16).replace("T", " ")}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* 7 engine cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3"
                        data-testid="compliance-health-engine-cards">
                        {(data.per_engine || []).map((e) => (
                            <Link to={ENGINE_LINKS[e.engine] || "#"} key={e.engine}
                                className="border rounded p-3 bg-white hover:shadow transition-shadow"
                                data-testid={`compliance-engine-card-${e.engine}`}>
                                <div className="text-[10px] uppercase text-slate-500 tracking-wider">
                                    {ENGINE_LABELS[e.engine] || e.engine}
                                </div>
                                <div className={`text-xs font-semibold mt-1 ${ENGINE_STATUS_COLORS[e.status] || ""}`}>
                                    {e.status.replace(/_/g, " ")}
                                </div>
                                <div className="text-[11px] text-slate-600 mt-2 font-mono">
                                    {e.completed}/{e.expected_to_date} done
                                </div>
                                {e.overdue_count > 0 && (
                                    <div className="text-[10px] text-rose-700 mt-1">
                                        ⚠ {e.overdue_count} overdue
                                    </div>
                                )}
                            </Link>
                        ))}
                    </div>

                    {/* Buckets: Overdue / 7d / 30d / Current Quarter */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
                        data-testid="compliance-health-buckets">
                        <BucketStrip title="Overdue" icon={<XCircle size={14} />}
                            entries={data.overdue} accent="rose"
                            testIdRoot="compliance-bucket-overdue" />
                        <BucketStrip title="Next 7 Days" icon={<Warning size={14} />}
                            entries={data.upcoming_7d} accent="amber"
                            testIdRoot="compliance-bucket-7d" />
                        <BucketStrip title="Next 30 Days" icon={<ClockCounterClockwise size={14} />}
                            entries={data.upcoming_30d} accent="blue"
                            testIdRoot="compliance-bucket-30d" />
                        <BucketStrip title="Current Quarter" icon={<CheckCircle size={14} />}
                            entries={data.current_quarter} accent="emerald"
                            testIdRoot="compliance-bucket-quarter" />
                    </div>

                    {/* Filing calendar table */}
                    <div className="border rounded">
                        <div className="p-2 bg-slate-50 border-b text-sm font-semibold flex justify-between">
                            <span>Filing Calendar · {data.filing_calendar?.length || 0} entries</span>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            <table className="w-full text-xs" data-testid="compliance-health-calendar-table">
                                <thead className="bg-slate-50 border-b text-slate-600 uppercase text-[10px] sticky top-0">
                                    <tr>
                                        <th className="text-left px-2 py-1">Engine</th>
                                        <th className="text-left px-2 py-1">Label</th>
                                        <th className="text-left px-2 py-1">Period</th>
                                        <th className="text-left px-2 py-1">State</th>
                                        <th className="text-left px-2 py-1">Due Date</th>
                                        <th className="text-right px-2 py-1">Days to Due</th>
                                        <th className="text-left px-2 py-1">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.filing_calendar || []).map((c, i) => (
                                        <tr key={i} className="border-b hover:bg-slate-50/50"
                                            data-testid={`compliance-calendar-row-${i}`}>
                                            <td className="px-2 py-1 font-mono">{ENGINE_LABELS[c.engine] || c.engine}</td>
                                            <td className="px-2 py-1">{c.label}</td>
                                            <td className="px-2 py-1 font-mono text-[10px]">{c.period_label}</td>
                                            <td className="px-2 py-1 text-[10px]">{c.state_code || "—"}</td>
                                            <td className="px-2 py-1 font-mono text-[10px]">{c.due_date}</td>
                                            <td className={`px-2 py-1 text-right font-mono ${c.days_to_due < 0 ? "text-rose-700" : ""}`}>
                                                {c.days_to_due}
                                            </td>
                                            <td className="px-2 py-1">
                                                <Badge className={STATUS_COLORS[c.status] || ""}>
                                                    {c.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Exceptions */}
                    <div className="border rounded">
                        <div className="p-2 bg-slate-50 border-b text-sm font-semibold">
                            Compliance Exceptions · {data.exceptions?.length || 0}
                        </div>
                        <table className="w-full text-xs" data-testid="compliance-health-exceptions-table">
                            <thead className="bg-slate-50 border-b text-slate-600 uppercase text-[10px]">
                                <tr>
                                    <th className="text-left px-2 py-1">Severity</th>
                                    <th className="text-left px-2 py-1">Category</th>
                                    <th className="text-left px-2 py-1">Entity</th>
                                    <th className="text-left px-2 py-1">Detail</th>
                                    <th className="text-left px-2 py-1">Resolve</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(!data.exceptions || data.exceptions.length === 0) && (
                                    <tr><td colSpan="5" className="text-center text-emerald-600 p-6 text-base">
                                        <CheckCircle size={16} className="inline mr-2" /> No exceptions detected
                                    </td></tr>
                                )}
                                {(data.exceptions || []).map((x, i) => (
                                    <tr key={i} className="border-b" data-testid={`compliance-exception-row-${i}`}>
                                        <td className="px-2 py-1">
                                            <Badge className={x.severity === "ERROR" ?
                                                "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}>
                                                {x.severity}
                                            </Badge>
                                        </td>
                                        <td className="px-2 py-1 font-mono text-[10px]">{x.category}</td>
                                        <td className="px-2 py-1">
                                            <div className="font-mono text-[10px]">{x.entity_code}</div>
                                            <div className="text-slate-600">{x.entity_name}</div>
                                        </td>
                                        <td className="px-2 py-1 text-[11px] text-slate-700">{x.detail}</td>
                                        <td className="px-2 py-1">
                                            {x.resolve_link ? (
                                                <Link to={x.resolve_link} className="text-blue-600 hover:underline text-[11px]"
                                                    data-testid={`compliance-exception-resolve-${i}`}>
                                                    Resolve →
                                                </Link>
                                            ) : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}


function BucketStrip({ title, icon, entries, accent, testIdRoot }) {
    const accentMap = {
        rose: "border-rose-200 bg-rose-50/30",
        amber: "border-amber-200 bg-amber-50/30",
        blue: "border-blue-200 bg-blue-50/30",
        emerald: "border-emerald-200 bg-emerald-50/30",
    };
    return (
        <div className={`border rounded p-3 ${accentMap[accent] || ""}`} data-testid={testIdRoot}>
            <div className="flex items-center gap-1 text-xs font-semibold text-slate-700">
                {icon} {title} ({entries?.length || 0})
            </div>
            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {(entries || []).slice(0, 8).map((e, i) => (
                    <div key={i} className="text-[10px] font-mono text-slate-700"
                        data-testid={`${testIdRoot}-item-${i}`}>
                        <span className="inline-block w-12 truncate">{e.due_date}</span>
                        {" "}{e.label}
                    </div>
                ))}
                {(entries?.length || 0) === 0 && (
                    <div className="text-[10px] text-slate-400 italic">No entries</div>
                )}
                {(entries?.length || 0) > 8 && (
                    <div className="text-[10px] text-slate-500">+{entries.length - 8} more</div>
                )}
            </div>
        </div>
    );
}
